import relationshipRoles from './relationshipRoles';
import { debugLog } from './logging/debugLog';

const normalizeMessageQuotes = (message = '') =>
  message.replace(/[‘’]/g, "'").replace(/[“”]/g, '"');

const splitNames = (value = '') =>
  value
    .split(/\s*(?:,|(?:\band\b))\s*/i)
    .map((name) => name.trim())
    .filter(Boolean);

const dedupeEntities = (entities = []) => {
  const seen = new Set();

  return entities.filter((entity) => {
    const key = `${entity.type}:${entity.role}:${entity.name.toLowerCase()}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const RELATIONSHIP_MODIFIER_PATTERN =
  '(?:(?:oldest|youngest|older|younger|eldest|middle)\\s+)?';

const NON_NAME_WORDS = new Set([
  'he',
  'she',
  'they',
  'them',
  'him',
  'her',
  'his',
  'hers',
  'their',
  'theirs',
]);

const isLikelyPersonName = (value = '') => {
  const cleaned = String(value).trim().replace(/[.,!?]+$/g, '');
  if (!cleaned) return false;

  const lower = cleaned.toLowerCase();
  if (NON_NAME_WORDS.has(lower)) return false;

  return /^[A-Z][a-zA-Z'’-]*(?:\s+[A-Z][a-zA-Z'’-]*)*$/.test(cleaned);
};

const extractEntities = (message = '') => {
  const entities = [];
  const normalizedMessage = normalizeMessageQuotes(message);

  relationshipRoles.forEach(({ keyword, aliases = [], role }) => {
    const allTerms = [keyword, ...aliases];

    allTerms.forEach((term) => {
      const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

      // Pattern 1: My daughter's name is Sarah
      // Pattern 1b: My oldest daughter's name is Sarah
      const possessivePattern = new RegExp(
        `\\bmy\\s+${RELATIONSHIP_MODIFIER_PATTERN}${escapedTerm}(?:'s|s)?\\s+name(?:s)?\\s+(?:is|are|was|=)?\\s*([A-Z][a-zA-Z'’-]*(?:\\s*(?:,\\s*|\\band\\b\\s*)[A-Z][a-zA-Z'’-]*)*)`,
        'gi'
      );

      let match;
      while ((match = possessivePattern.exec(normalizedMessage)) !== null) {
        const names = splitNames(match[1]);

        names.forEach((name) => {
          if (isLikelyPersonName(name)) {
            entities.push({ type: 'relationship', role, name });
          }
        });
      }

      // Pattern 2: Marc and Anthony are my brothers / Carole is my friend
      const inversePattern = new RegExp(
        `\\b([A-Z][a-zA-Z'’-]*(?:\\s*(?:,\\s*|\\band\\b\\s*)[A-Z][a-zA-Z'’-]*)*)\\s+(?:is|are|was|were)\\s+my\\s+${RELATIONSHIP_MODIFIER_PATTERN}${escapedTerm}(?:'s|s)?\\b`,
        'gi'
      );

      while ((match = inversePattern.exec(normalizedMessage)) !== null) {
        const names = splitNames(match[1]);

        names.forEach((name) => {
          if (isLikelyPersonName(name)) {
            entities.push({ type: 'relationship', role, name });
          }
        });
      }

      // Pattern 3: My brothers are Marc and Anthony / My friend is Carole
      // Pattern 3b: My oldest daughter is Samantha
      const pluralIntroPattern = new RegExp(
        `\\bmy\\s+${RELATIONSHIP_MODIFIER_PATTERN}${escapedTerm}(?:'s|s)?\\s+(?:is|are|was|were)\\s+([A-Z][a-zA-Z'’-]*(?:\\s*(?:,\\s*|\\band\\b\\s*)[A-Z][a-zA-Z'’-]*)*)`,
        'gi'
      );

      while ((match = pluralIntroPattern.exec(normalizedMessage)) !== null) {
        const names = splitNames(match[1]);

        names.forEach((name) => {
          if (isLikelyPersonName(name)) {
            entities.push({ type: 'relationship', role, name });
          }
        });
      }
    });
  });

  return dedupeEntities(entities);
};

const buildContext = (message = '', reply = '', memories = []) => {
  const entities = extractEntities(message);
  debugLog('🧪 Extracted entities:', entities);

  const context = {
    tone: 'neutral',
    mood: 'curious',
    urgency: 'normal',
    energy: 'medium',
    entities,
  };

  const cleanMemories = memories.filter(
    (mem) => mem.type !== 'injected' && mem.origin !== 'search'
  );

  if (cleanMemories.length) {
    const recentMessages = cleanMemories
      .slice(0, 5)
      .map((mem) => mem.message)
      .join(' ');
    context.shapedRecents = `Recently, the user has shared things like: ${recentMessages}`;
  }

  return context;
};

export default buildContext;

